import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Clock, Eye, Tag, Calendar, User, Share2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/useSEO";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function renderMarkdown(content) {
  if (!content) return "";
  return content
    .split("\n\n")
    .map((block, i) => {
      if (block.startsWith("### ")) {
        return `<h3 class="text-xl font-heading font-light text-void mt-8 mb-3">${block.slice(4)}</h3>`;
      }
      if (block.startsWith("## ")) {
        return `<h2 class="text-2xl font-heading font-light text-void mt-10 mb-4">${block.slice(3)}</h2>`;
      }
      if (block.startsWith("# ")) {
        return `<h1 class="text-3xl font-heading font-light text-void mt-10 mb-4">${block.slice(2)}</h1>`;
      }

      const lines = block.split("\n");
      const isList = lines.every(
        (l) => l.startsWith("- ") || l.startsWith("* ") || /^\d+\.\s/.test(l) || l.trim() === ""
      );

      if (isList) {
        const isOrdered = lines.some((l) => /^\d+\.\s/.test(l));
        const items = lines
          .filter((l) => l.trim())
          .map((l) => {
            const text = l.replace(/^[-*]\s|^\d+\.\s/, "");
            return `<li class="text-void/80 font-body leading-relaxed">${formatInline(text)}</li>`;
          })
          .join("");
        return isOrdered
          ? `<ol class="list-decimal list-inside space-y-2 my-4 ml-4">${items}</ol>`
          : `<ul class="list-disc list-inside space-y-2 my-4 ml-4">${items}</ul>`;
      }

      return `<p class="text-void/80 font-body leading-relaxed my-4">${formatInline(block.replace(/\n/g, " "))}</p>`;
    })
    .join("");
}

function formatInline(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-void font-medium">$1</strong>')
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [hreflangLinks, setHreflangLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
        // Fetch related posts in same language
        const postLang = response.data.language || "fi";
        const allRes = await axios.get(`${API}/blog?language=${postLang}`);
        const others = (allRes.data.posts || []).filter(p => p.slug !== slug).slice(0, 3);
        setRelatedPosts(others);

        // Build hreflang links from articles with same cover image (= same topic)
        try {
          const postImage = response.data.cover_image;
          const postLang2 = response.data.language || "fi";
          if (postImage) {
            const allLangsRes = await axios.get(`${API}/blog?limit=100`);
            const allPosts = allLangsRes.data.posts || allLangsRes.data || [];
            const siteUrl = "https://unipäiväkirja.com";
            const links = [];
            allPosts.forEach(p => {
              if (p.slug === slug) return;
              if (p.language === postLang2) return;
              if (p.cover_image === postImage) {
                links.push({ lang: p.language, url: `${siteUrl}/blog/${p.slug}` });
              }
            });
            links.push({ lang: postLang2, url: `${siteUrl}/blog/${slug}` });
            links.push({ lang: "x-default", url: `${siteUrl}/blog/${slug}` });
            setHreflangLinks(links);
          }
        } catch (e) {
          console.error("Failed to build hreflang links:", e);
        }
      } catch (error) {
        console.error("Failed to fetch blog post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === "fi"
        ? "fi-FI"
        : i18n.language === "fr"
        ? "fr-FR"
        : i18n.language === "de"
        ? "de-DE"
        : i18n.language === "es"
        ? "es-ES"
        : "en-US",
      { day: "numeric", month: "long", year: "numeric" }
    );
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.round(words / 200));
  };

  const siteUrl = "https://unipäiväkirja.com";
  const articleUrl = post ? `${siteUrl}/blog/${post.slug}` : `${siteUrl}/blog`;
  const readTime = post ? estimateReadTime(post.content) : 1;

  useSEO(post ? {
    title: `${post.title} | Uneksia Blogi`,
    description: post.excerpt,
    ogTitle: post.title,
    ogDescription: post.excerpt,
    ogImage: post.cover_image,
    ogType: "article",
    ogUrl: articleUrl,
    canonical: articleUrl,
    language: post.language || "fi",
    hreflangLinks: hreflangLinks,
    articleMeta: {
      publishedTime: post.created_at,
      modifiedTime: post.updated_at,
      author: post.author_name,
      tags: post.tags
    },
    schemaData: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.cover_image,
      "url": articleUrl,
      "datePublished": post.created_at,
      "dateModified": post.updated_at,
      "author": { "@type": "Person", "name": post.author_name },
      "publisher": {
        "@type": "Organization",
        "name": "Uneksia",
        "logo": { "@type": "ImageObject", "url": `${siteUrl}/logo-new.png` }
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl },
      "wordCount": post.content?.split(/\s+/).length || 0,
      "timeRequired": `PT${readTime}M`,
      "keywords": post.tags?.join(", "),
      "inLanguage": post.language || "fi"
    }
  } : {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-green-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-void/20 border-t-void rounded-full mx-auto" />
          <p className="text-void/60 font-body mt-4">{t("blog.loading")}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-green-100 flex items-center justify-center">
        <div className="text-center glass-effect rounded-3xl p-12">
          <p className="text-void/60 font-body text-lg mb-4">{t("blog.notFound")}</p>
          <Button
            onClick={() => navigate("/blog")}
            className="bg-void text-white hover:bg-void/90 rounded-full px-6 py-4"
            data-testid="blog-back-to-list"
          >
            {t("blog.backToList")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-green-100">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/blog")}
                className="flex items-center gap-2 text-void hover:text-void/70 transition-colors"
                data-testid="article-back-button"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-body text-sm hidden sm:inline">{t("blog.title")}</span>
              </button>
              <Link to="/" className="flex items-center gap-2 ml-2" data-testid="article-home-link">
                <img src="/logo-new.png" alt="Uneksia" className="w-8 h-8 rounded-full" />
                <span className="font-heading text-sm text-void hidden sm:inline">Uneksia</span>
              </Link>
            </div>
            <div className="flex items-center gap-3 sm:gap-5">
              <Link
                to="/#free-trial"
                className="font-body text-sm text-void/70 hover:text-void transition-colors hidden sm:inline"
                data-testid="article-how-it-works"
              >
                {t("trial.tryAiAnalysis")}
              </Link>
              <LanguageSelector variant="minimal" />
            </div>
          </div>
        </div>
      </nav>

      {/* Cover Image */}
      {post.cover_image && (
        <div className="w-full h-64 sm:h-96 overflow-hidden relative">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent pointer-events-none" />
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {post.tags?.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-ethereal/20 text-void/70 text-sm rounded-full font-body flex items-center gap-1"
            >
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
        </div>

        {/* Title */}
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-heading font-light text-void tracking-tight leading-tight mb-6"
          data-testid="article-title"
        >
          {post.title}
        </h1>

        {/* Info Bar */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-void/50 font-body mb-6 pb-6 border-b border-void/10">
          <span className="flex items-center gap-1">
            <User className="w-4 h-4" />
            {post.author_name}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(post.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {estimateReadTime(post.content)} min {t("blog.readTime")}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            {(post.views || 0) + 1}
          </span>
        </div>

        {/* Share Buttons */}
        <div className="flex flex-wrap items-center gap-3 mb-10 pb-8 border-b border-void/10">
          <span className="text-sm text-void/50 font-body flex items-center gap-1">
            <Share2 className="w-4 h-4" />
            {t("blog.share")}:
          </span>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1877F2]/10 text-[#1877F2] rounded-full text-sm font-body hover:bg-[#1877F2]/20 transition-colors"
            data-testid="share-facebook"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Facebook
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(post.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1DA1F2]/10 text-[#1DA1F2] rounded-full text-sm font-body hover:bg-[#1DA1F2]/20 transition-colors"
            data-testid="share-twitter"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            X
          </a>
          <a
            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + ' ' + articleUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366]/10 text-[#25D366] rounded-full text-sm font-body hover:bg-[#25D366]/20 transition-colors"
            data-testid="share-whatsapp"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
          <button
            onClick={() => {
              navigator.clipboard.writeText(articleUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-body transition-colors ${
              copied ? 'bg-green-100 text-green-700' : 'bg-void/5 text-void/60 hover:bg-void/10'
            }`}
            data-testid="share-copy-link"
          >
            <ExternalLink className="w-4 h-4" />
            {copied ? t("blog.linkCopied") : t("blog.copyLink")}
          </button>
        </div>

        {/* Content */}
        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
        />

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16 mb-8">
            <h3 className="text-2xl font-heading font-light text-void mb-6">
              {t("blog.relatedPosts")}
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {relatedPosts.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/blog/${rp.slug}`}
                  className="group glass-effect rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`related-post-${rp.slug}`}
                >
                  {rp.cover_image && (
                    <div className="h-32 overflow-hidden">
                      <img
                        src={rp.cover_image}
                        alt={rp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h4 className="font-heading text-sm font-light text-void group-hover:text-ethereal transition-colors line-clamp-2 mb-1">
                      {rp.title}
                    </h4>
                    <span className="text-xs text-void/40 font-body flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {estimateReadTime(rp.content)} min
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 glass-effect rounded-3xl p-8 text-center">
          <h3 className="text-2xl font-heading font-light text-void mb-3">
            {t("blog.articleCta")}
          </h3>
          <p className="text-void/70 font-body mb-6">{t("blog.articleCtaSubtitle")}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => navigate(token ? "/journal" : "/auth")}
              className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-4 font-body transition-all duration-300 hover:scale-105"
              data-testid="article-start-journal-button"
            >
              {t("blog.ctaButton")}
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="rounded-full px-6 py-4 font-body border-void/20 text-void hover:bg-void/5"
              data-testid="article-explore-app-button"
            >
              {t("blog.exploreApp") || "Tutustu sovellukseen"}
            </Button>
            <Button
              onClick={() => navigate("/blog")}
              variant="outline"
              className="rounded-full px-6 py-4 font-body border-void/20 text-void hover:bg-void/5"
              data-testid="article-more-posts-button"
            >
              {t("blog.morePosts")}
            </Button>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-void/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Uneksia" className="w-6 h-6" />
              <span className="font-heading text-void">{t("appName")}</span>
            </Link>
            <p className="text-sm text-void/60 font-body">{t("footer.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
